import "./super-agent-user.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomBreadcrumb } from "components/elements/custom-breadcrumb/custom-breadcrumb";
import { CustomTable } from "components/elements/custom-table/custom-table";
import { API_SERVICE } from "api/service";
import { CustomLoader } from "components/elements/custom-loader/custom-loader";
import { CustomModal } from "components/elements/custom-modal/custom-modal";
import { DetailsBreakDown } from "components/elements/details-break-down/details-break-down";
import { CustomConfirmDialog } from "../../../components/elements/custom-confirm-dialog/custom-confirm-dialog";
import { Toast } from "primereact/toast";
import { CreateSuperAgentUser } from "./create-super-agent-user";
import { HELPER } from "utils/helper";
import { CustomToast } from "components/elements/alert/custom-toast";
import { ResetPassword } from "./reset-password";
import { ChangeUserStatus } from "./change-user-status";
import { SuperAgentUsersFilter } from "./super-agent-users-filter";
import { UserManualViewerButton } from "components/elements/user-manual-viewer/user-manual-viewer-button";
import ErrorBoundary from "components/elements/error-boundary/error-boundary";
import { Optional } from "components/elements/optional/optional";
import { BACK_OFFICE_API } from "../../../api/routes/back-office/index";

export function SuperAgentUsers() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [confirmText] = useState("");
  const [itemIdForDelete] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [range] = useState(5);
  const [breakDownTitle, setBreakDownTitle] = useState("");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [detailsId, setDetailsId] = useState(null);
  const [detailsMobile, setDetailsMobile] = useState(false);

  const [emptyText, setEmptyText] = useState("");
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [pageNo, setPageNo] = useState(0);
  const [isSearch, setIsSearch] = useState(false);
  const [searchParams, setSearchParams] = useState(null);

  const tableHeaders = [
    { label: "Firstname", value: "firstname" },
    { label: "Lastname", value: "lastname" },
    { label: "Super Agent", value: "merchant" },
    { label: "Status", value: "active" },
    { label: "Date", value: "createdAt" },
    { label: "Actions", value: "actions" },
  ];

  const [details, setDetails] = useState([]);

  const [merchantUsers, setMerchantUsers] = useState([]);
  const [isPasswordReset, setIsPasswordReset] = useState(false);

  function deleteChargeType(e) {
    setModalLoading(true);
  }

  function reloadDetails() {
    viewMerchantUser(detailsId, detailsMobile);
  }

  const viewMerchantUser = useCallback((e, isMobile) => {
    setDetailsError(null);
    setDetailsId(e);
    setDetailsMobile(isMobile);
    setDetailsLoading(true);
    openModal(2, isMobile);
    let arr = [];
    const payload = {
      username: e.username,
    };
    const url = BACK_OFFICE_API.SUPER_AGENT_USER.VIEW_SUPER_AGENT_USER;
    API_SERVICE.MAKE_PUT_REQUEST(url, payload)
      .then((data) => {
        const result = data.result;
        setDetails([]);
        arr.push({ label: "First name", value: result?.firstname });
        arr.push({ label: "Last name", value: result?.lastname });
        arr.push({ label: "Username", value: result?.username });
        arr.push({ label: "Email", value: result?.email });
        arr.push({
          label: "Status",
          value: result?.active ? "Active" : "Inactive",
          itemCase: "active",
        });
        arr.push({
          label: "Created Date",
          value: result?.createdAt,
          itemCase: "transactionTime",
        });
        arr.push({ label: "Super agent", value: result?.merchantName });
        arr.push({
          label: "Super agent Id",
          value: result?.merchantId,
        });
        setDetails(arr);
        setBreakDownTitle("Super Agent User");
        setDetailsLoading(false);
      })
      .catch((error) => {
        setDetailsError(HELPER.PROCESS_ERROR(error));
        setDetailsLoading(false);
      });
  }, []);

  const openAction = useCallback(
    (e, action, isMobile) => {
      // eslint-disable-next-line default-case
      switch (action) {
        case "VIEW": {
          viewMerchantUser(e, isMobile);
          break;
        }
      }
    },
    [viewMerchantUser]
  );

  function goToPage(pageNo) {
    setPageNo(pageNo);
  }

  const getMerchantUsers = useCallback(() => {
    setCurrentIndex(0);
    setLoading(true);
    setMerchantUsers([]);
    const params = HELPER.TO_URL_STRING({
      page: pageNo,
      size: 10,
    });
    const url = BACK_OFFICE_API.SUPER_AGENT_USER.GET_SUPER_AGENT_USERS;
    API_SERVICE.MAKE_GET_REQUEST(`${url}?${params}`)
      .then((data) => {
        const result = data.result.content;
        if (!result.length) {
          setEmptyText("No super agent user found ...");
        } else {
          let arr = [];
          setTotalItems(data.result.totalElements);
          setTotalPages(data.result.totalPages);
          result.forEach((e) => {
            arr.push({
              ...e,
              merchant: e?.merchantName,
              merchantId: e?.merchantId,
              active: e?.active ? "Active" : "Inactive",
              actions: "VIEW",
              detailsFunction: openAction,
            });
          });
          setMerchantUsers(arr);
        }
        setError(null);
        setCurrentIndex(1);
        setLoading(false);
      })
      .catch((error) => {
        setError(HELPER.PROCESS_ERROR(error));
        setCurrentIndex(1);
        setLoading(false);
      });
  }, [pageNo, openAction]);

  const searchMerchantUsers = useCallback(
    (payload) => {
      setCurrentIndex(0);
      setVisible(false);
      setLoading(true);
      setIsSearch(true);
      setMerchantUsers([]);
      const params = HELPER.TO_URL_STRING({
        page: pageNo,
        size: 10,
      });
      const url = BACK_OFFICE_API.SUPER_AGENT_USER.FILTER_SUPER_AGENT_USERS;
      API_SERVICE.MAKE_PUT_REQUEST(`${url}?${params}`, payload)
        .then((data) => {
          const result = data.result.content;
          if (!result.length) {
            setEmptyText("No super agent user found ...");
          } else {
            let arr = [];
            setTotalItems(data.result.totalElements);
            setTotalPages(data.result.totalPages);
            result.forEach((e) => {
              arr.push({
                ...e,
                merchant: e?.merchantName,
                merchantId: e?.merchantId,
                active: e?.active ? "Active" : "Inactive",
                actions: "VIEW",
                detailsFunction: openAction,
              });
            });
            setMerchantUsers(arr);
          }
          setError(null);
          setCurrentIndex(1);
          setLoading(false);
        })
        .catch((error) => {
          setError(HELPER.PROCESS_ERROR(error));
          setCurrentIndex(1);
          setLoading(false);
        });
    },
    [pageNo, openAction]
  );

  useEffect(() => {
    if (searchParams) {
      searchMerchantUsers(searchParams);
    } else {
      getMerchantUsers();
    }
  }, [searchMerchantUsers, getMerchantUsers, searchParams]);

  function onHide() {}

  function openModal(index, isMobile) {
    setCurrentModalIndex(index);
    if (isMobile) {
      setCurrentIndex(2);
    } else {
      setVisible(true);
    }
  }

  function showResetPasswordModal() {
    setCurrentModalIndex(4);
  }

  function showChangeStatusModal() {
    setCurrentModalIndex(5);
  }

  function reload() {
    setPageNo(0);
    setSearchParams(null);
    setLoading(true);
    // setSearch(false);
    setIsSearch(false);
    setCurrentIndex(0);
    getMerchantUsers();
  }

  function closeModal(isReload) {
    if (visible) {
      setVisible(false);
    }
    if (currentIndex === 2) {
      setCurrentIndex(1);
    }

    if (isReload === true) {
      reload();
    }
  }

  function saveSearchParams(params) {
    setPageNo(0);
    setSearchParams(params);
  }

  const mobileModal = () => {
    // eslint-disable-next-line default-case
    switch (currentModalIndex) {
      case 0:
        return <CreateSuperAgentUser closeModal={closeModal} />;
      case 1:
        return (
          <SuperAgentUsersFilter
            searchFunction={saveSearchParams}
            isSearch={true}
            closeModal={closeModal}
          />
        );
      case 2:
        return (
          <DetailsBreakDown
            reload={reloadDetails}
            error={detailsError}
            loading={detailsLoading}
            title={breakDownTitle}
            breakDown={details}
            closeModal={closeModal}
            isResetPassword={true}
            resetPassword={showResetPasswordModal}
            isChangeStatus={true}
            changeStatus={showChangeStatusModal}
            userStatus={detailsId.active}
          />
        );
      case 3:
        return (
          <CustomConfirmDialog
            itemId={itemIdForDelete}
            confirmText={confirmText}
            loading={modalLoading}
            loadingText="Deleting charge type..."
            fn={deleteChargeType}
            closeModal={closeModal}
          />
        );
      case 4:
        return (
          <ResetPassword userDetails={detailsId} closeModal={closeModal} />
        );
      case 5:
        return (
          <ChangeUserStatus userDetails={detailsId} closeModal={closeModal} />
        );
    }
  };

  const merchantUsersView = () => {
    // eslint-disable-next-line default-case
    switch (currentIndex) {
      case 0:
        return (
          <div className="loading-container">
            <CustomLoader loadingText="Loading super agent users..." />
          </div>
        );
      case 1:
        return (
          <CustomTable
            isReload={true}
            totalPages={totalPages}
            totalItems={totalItems}
            currentPage={pageNo + 1}
            range={range}
            emptyText={emptyText}
            search={isSearch}
            reload={reload}
            error={error}
            items={merchantUsers}
            headers={tableHeaders}
            nextPage={() => setPageNo(pageNo + 1)}
            prevPage={() => setPageNo(pageNo - 1)}
            goToFirstPage={() => setPageNo(0)}
            goToLastPage={() => setPageNo(totalPages - 1)}
            goToPage={goToPage}
          />
        );
      case 2:
        return (
          <div className="mobile-modal-container">
            <div className="custom-card">
              <div className="mobile-card-position">{mobileModal()}</div>
            </div>
          </div>
        );
    }
  };

  const modalContent = () => {
    // eslint-disable-next-line default-case
    switch (currentModalIndex) {
      case 0:
        return <CreateSuperAgentUser closeModal={closeModal} />;
      case 1:
        return (
          <SuperAgentUsersFilter
            searchFunction={saveSearchParams}
            isSearch={true}
            closeModal={closeModal}
          />
        );
      case 2:
        return (
          <DetailsBreakDown
            reload={reloadDetails}
            error={detailsError}
            loading={detailsLoading}
            title={breakDownTitle}
            breakDown={details}
            closeModal={closeModal}
            isResetPassword={true}
            resetPassword={showResetPasswordModal}
            isChangeStatus={true}
            changeStatus={showChangeStatusModal}
            userStatus={detailsId.active}
          />
        );
      case 3:
        return (
          <CustomConfirmDialog
            itemId={itemIdForDelete}
            confirmText={confirmText}
            loading={modalLoading}
            loadingText="Deleting charge type..."
            fn={deleteChargeType}
            closeModal={closeModal}
          />
        );
      case 4:
        return (
          <ResetPassword userDetails={detailsId} closeModal={closeModal} />
        );
      case 5:
        return (
          <ChangeUserStatus userDetails={detailsId} closeModal={closeModal} />
        );
    }
  };

  return (
    <ErrorBoundary>
      <div>
        <Toast ref={toast} />
        <div>
          <CustomModal
            closeModal={closeModal}
            onHide={onHide}
            visible={visible}
            modalContent={modalContent}
          />
        </div>
        <div style={{ display: isPasswordReset ? "block" : "none" }}>
          <CustomToast
            title="Success"
            description="Password successfully reset!"
            type="success"
            clearToast={() => setIsPasswordReset(false)}
          />
        </div>
        {/* <div className="page-title p-text-left">Super agent Users</div> */}
        <div className="page-header">
          <div className="page-title p-text-left">Super Agent Users</div>
          <UserManualViewerButton fileName="manage_users_superagent_user" />
        </div>
        <div className="p-mt-2">
          <CustomBreadcrumb page={"Manage Super Agent users"} />
        </div>
        <div className="floating-buttons desktop-scree">
          <div className="p-grid">
            <div className="p-col-7"></div>
            <div className="p-col-5">
              <div className="p-grid">
                <div className="p-col-6 p-d-flex p-jc-end p-ai-center">
                  <button
                    disabled={loading || merchantUsers?.length === 0}
                    onClick={() => openModal(1, false)}
                    className="filter-button"
                  >
                    <i className="pi pi-filter" />
                    <span className="hide-btn-text"> Filter</span>
                  </button>
                </div>
                <div className="p-col-6">
                  <Optional
                    show={HELPER.HAS_AUTHORITY("bo_dcir_create_merchant_users")}
                  >
                    <button
                      disabled={loading}
                      onClick={() => openModal(0, false)}
                      className="primary-button hide-btn-text"
                    >
                      <i className="pi pi-plus" />
                      <span className="hide-btn-text"> New user </span>
                    </button>
                  </Optional>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>{merchantUsersView()}</div>
      </div>
    </ErrorBoundary>
  );
}
